import { useState, useCallback } from "react";
import { useUserFetchCache } from "./useUserFetchCache";
import { httpRequest } from "../utils/httpClient";
import { API_BASE } from "../config/api";
import { authService } from "../services/auth";

export function useSimplifySession(_user) {
  const { getCachedPromise, setCachedPromise, removeCachedPromise } = useUserFetchCache();
  // Session tracking
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionGames, setSessionGames] = useState([]);
  const [showGameSelectionModal, setShowGameSelectionModal] = useState(false);

  // Let's Play modal state
  const [showLetSPlayModal, setShowLetSPlayModal] = useState(false);
  const [letSPlayGame, setLetSPlayGame] = useState(null);

  // Session form in Let's Play modal
  const [showSessionFormInLetSPlay, setShowSessionFormInLetSPlay] = useState(false);
  const [showRulesSummaryInLetSPlay, setShowRulesSummaryInLetSPlay] = useState(false);
  const [showScorePadInLetSPlay, setShowScorePadInLetSPlay] = useState(false);

  // Standalone session modal
  const [showSessionModal, setShowSessionModal] = useState(false);

  // Session data for logging
  const [sessionData, setSessionData] = useState({
    game: null,
    date: new Date().toISOString().split("T")[0],
    scores: {}
  });

  // User mapping for SessionForm
  const [userMap, setUserMap] = useState(new Map());

  const updateSessionData = useCallback((field, value) => {
    setSessionData(prev => ({ ...prev, [field]: value }));
  }, []);

  const setSessionGame = useCallback((game) => {
    setSessionData(prev => ({ ...prev, game }));
  }, []);

  const resetSessionData = useCallback(() => {
    setSessionData({
      game: null,
      date: new Date().toISOString().split("T")[0],
      scores: {}
    });
  }, []);

  const handleLetSPlay = useCallback((game) => {
    setLetSPlayGame(game);
    setShowLetSPlayModal(true);
    setShowSessionFormInLetSPlay(false);
    setShowRulesSummaryInLetSPlay(false);
    setShowScorePadInLetSPlay(false);
  }, []);

  const closeLetSPlayModal = useCallback(() => {
    setShowLetSPlayModal(false);
    setLetSPlayGame(null);
    setShowSessionFormInLetSPlay(false);
    setShowRulesSummaryInLetSPlay(false);
    setShowScorePadInLetSPlay(false);
  }, []);

  const handleLogSession = useCallback(() => {
    if (!letSPlayGame) return;
    setSessionData(prev => ({ ...prev, game: letSPlayGame }));
    setShowSessionFormInLetSPlay(true);
  }, [letSPlayGame]);

  const handleSeeRulesSummary = useCallback(() => {
    if (!letSPlayGame) return;
    setShowRulesSummaryInLetSPlay(true);
  }, [letSPlayGame]);

  const handleOpenScorePad = useCallback(() => {
    if (!letSPlayGame) return;
    setShowScorePadInLetSPlay(true);
  }, [letSPlayGame]);

  const handleCloseScorePad = useCallback(() => {
    setShowScorePadInLetSPlay(false);
  }, []);

  const resetSessionState = useCallback(() => {
    setCurrentSession(null);
    setSessionGames([]);
    setShowGameSelectionModal(false);
    setShowSessionModal(false);
    resetSessionData();
  }, [resetSessionData]);

  // Fetch user information with caching
  const fetchUserInfo = useCallback(async (userId) => {
    // Check cache first
    const cachedName = userMap.get(userId);
    if (cachedName && cachedName !== `User ${userId}`) {
      return cachedName;
    }

    // Prevent multiple simultaneous requests
    const cachedPromise = getCachedPromise(userId);
    if (cachedPromise) {
      return cachedPromise;
    }

    // Create and cache the fetch promise
    const fetchPromise = (async () => {
      try {
        // Try endpoints in order of likelihood
        const patterns = [
          `${API_BASE}/auth/me`,
          `${API_BASE}/users/${userId}`,
          `${API_BASE}/auth/users/${userId}`,
          `${API_BASE}/profile/${userId}`
        ];

        for (const pattern of patterns) {
          try {
            const res = await httpRequest(pattern, {
              method: "GET",
              headers: authService.getAuthHeaders()
            });

            if (res.ok) {
              const data = await res.json();
              let username = `User ${userId}`;

              if (data) {
                const isMatchingUser = pattern === `${API_BASE}/auth/me` && data.id === userId;
                const isOtherEndpoint = pattern !== `${API_BASE}/auth/me`;

                if (isMatchingUser || isOtherEndpoint) {
                  username = data.username || data.name || data.display_name || `User ${userId}`;
                }
              }

              setUserMap(prev => new Map(prev).set(userId, username));
              removeCachedPromise(userId);
              return username;
            }
          } catch (error) {
            continue;
          }
        }

        removeCachedPromise(userId);
        return `User ${userId}`;
      } catch (error) {
        removeCachedPromise(userId);
        return `User ${userId}`;
      }
    })();

    setCachedPromise(userId, fetchPromise);
    return fetchPromise;
  }, [getCachedPromise, removeCachedPromise, setCachedPromise, setUserMap, userMap]);

  return {
    // Session tracking
    currentSession,
    setCurrentSession,
    sessionGames,
    setSessionGames,
    showGameSelectionModal,
    setShowGameSelectionModal,
    // Let's Play
    showLetSPlayModal,
    setShowLetSPlayModal,
    letSPlayGame,
    setLetSPlayGame,
    showSessionFormInLetSPlay,
    setShowSessionFormInLetSPlay,
    showRulesSummaryInLetSPlay,
    setShowRulesSummaryInLetSPlay,
    showScorePadInLetSPlay,
    setShowScorePadInLetSPlay,
    handleLetSPlay,
    closeLetSPlayModal,
    handleLogSession,
    handleSeeRulesSummary,
    handleOpenScorePad,
    handleCloseScorePad,
    // Session modal
    showSessionModal,
    setShowSessionModal,
    // Session data
    sessionData,
    setSessionData,
    updateSessionData,
    setSessionGame,
    resetSessionData,
    // User mapping
    userMap,
    setUserMap,
    resetSessionState,
    // User info fetching
    fetchUserInfo
  };
}
